import { useState } from 'react'
import { View, StyleSheet } from 'react-native'
import { Modal, Portal, Text, Button, List } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import {
  NodeMember,
  useRemoveNodeMemberMutation,
  useTransferNodeOwnershipMutation,
  useUpdateNodeMemberMutation,
} from '../../../../generated/graphql'
import Toast from 'react-native-root-toast'
import * as Sentry from '@sentry/react-native'
import { useTheme } from '../../../../hooks/theme.hook'
import { useNavigation } from '@react-navigation/native'
import { AppNavigation } from '../../../../navigation/types'
import type { NodeMembersScreenNode } from './node-member.list'

interface MemberActionsModalProps {
  visible: boolean
  onClose: () => void
  node: NodeMembersScreenNode
  member: NodeMember
  onTransferSuccess: () => void
  onDeleteSuccess: () => void
}

export const MemberActionsModal = ({
  visible,
  onClose,
  node,
  member,
  onTransferSuccess,
  onDeleteSuccess,
}: MemberActionsModalProps) => {
  const { t } = useTranslation('nodes')
  const theme = useTheme()
  const [confirmAction, setConfirmAction] = useState<
    'transfer' | 'remove' | 'toggleAdmin' | null
  >(null)
  const navigation = useNavigation<AppNavigation>()
  const [doTransferOwnership, { loading: transferring }] =
    useTransferNodeOwnershipMutation({
      refetchQueries: ['nodeById', 'nodeMembersByNodeId'],
    })

  const [doRemoveMember, { loading: removing }] = useRemoveNodeMemberMutation({
    refetchQueries: ['nodeMembersByNodeId'],
  })

  const [doUpdateMember, { loading: updating }] = useUpdateNodeMemberMutation({
    refetchQueries: ['nodeMembersByNodeId'],
  })

  /** Owners may remove anyone; admins may not remove other admins (matches backend). */
  const canRemoveThisMember = node.isOwner || !member.isAdmin

  const handleToggleAdmin = async () => {
    try {
      await doUpdateMember({
        variables: {
          input: {
            id: member.id,
            isAdmin: !member.isAdmin,
          },
        },
      })
      Toast.show(
        member.isAdmin
          ? t('admin-removed-successfully')
          : t('admin-added-successfully')
      )
      setConfirmAction(null)
      onDeleteSuccess()
    } catch (error) {
      console.error('Error updating member role:', error)
      Sentry.captureException(error)
      Toast.show((error as Error).message)
    }
  }

  const handleTransferOwnership = async () => {
    try {
      await doTransferOwnership({
        variables: {
          input: {
            nodeId: node.id,
            newOwnerId: member.userId,
          },
        },
      })
      Toast.show(t('ownership-transferred-successfully'))
      setConfirmAction(null)
      navigation.replace('MyNodes')
      onTransferSuccess()
    } catch (error) {
      console.error('Error transferring ownership:', error)
      Sentry.captureException(error)
      Toast.show((error as Error).message)
    }
  }

  const handleRemoveMember = async () => {
    try {
      await doRemoveMember({
        variables: {
          id: member.id,
        },
      })
      Toast.show(t('member-removed-successfully'))
      setConfirmAction(null)
      onDeleteSuccess()
    } catch (error) {
      console.error('Error removing member:', error)
      Sentry.captureException(error)
      Toast.show((error as Error).message)
    }
  }

  if (confirmAction === 'toggleAdmin' && node.isOwner) {
    return (
      <Portal>
        <Modal
          visible={visible}
          onDismiss={onClose}
          contentContainerStyle={[
            styles.modalContainer,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Text variant="titleLarge" style={styles.title}>
            {member.isAdmin ? t('remove-admin') : t('make-admin')}
          </Text>
          <Text variant="bodyMedium" style={styles.description}>
            {member.isAdmin
              ? t('remove-admin-confirm', { username: member.user.username })
              : t('make-admin-confirm', { username: member.user.username })}
          </Text>
          <View style={styles.buttonContainer}>
            <Button
              onPress={() => {
                setConfirmAction(null)
                onClose()
              }}
              disabled={updating}
            >
              {t('cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleToggleAdmin}
              loading={updating}
              disabled={updating}
            >
              {member.isAdmin ? t('remove') : t('promote')}
            </Button>
          </View>
        </Modal>
      </Portal>
    )
  }

  if (confirmAction === 'transfer' && node.isOwner) {
    return (
      <Portal>
        <Modal
          visible={visible}
          onDismiss={onClose}
          contentContainerStyle={[
            styles.modalContainer,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Text variant="titleLarge" style={styles.title}>
            {t('transfer-ownership')}
          </Text>
          <Text variant="bodyMedium" style={styles.description}>
            {t('transfer-ownership-confirm', {
              username: member.user.username,
            })}
          </Text>
          <Text variant="bodySmall" style={styles.warning}>
            {t('transfer-ownership-warning')}
          </Text>
          <View style={styles.buttonContainer}>
            <Button
              onPress={() => {
                setConfirmAction(null)
                onClose()
              }}
              disabled={transferring}
            >
              {t('cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleTransferOwnership}
              loading={transferring}
              disabled={transferring}
            >
              {t('transfer')}
            </Button>
          </View>
        </Modal>
      </Portal>
    )
  }

  if (confirmAction === 'remove' && canRemoveThisMember) {
    return (
      <Portal>
        <Modal
          visible={visible}
          onDismiss={onClose}
          contentContainerStyle={[
            styles.modalContainer,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Text variant="titleLarge" style={styles.title}>
            {t('remove-member')}
          </Text>
          <Text variant="bodyMedium" style={styles.description}>
            {t('remove-member-confirm', { username: member.user.username })}
          </Text>
          <View style={styles.buttonContainer}>
            <Button
              onPress={() => {
                setConfirmAction(null)
                onClose()
              }}
              disabled={removing}
            >
              {t('cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleRemoveMember}
              loading={removing}
              disabled={removing}
              buttonColor={theme.colors.error}
            >
              {t('remove')}
            </Button>
          </View>
        </Modal>
      </Portal>
    )
  }

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onClose}
        contentContainerStyle={[
          styles.modalContainer,
          { backgroundColor: theme.colors.surface },
        ]}
      >
        <Text variant="titleLarge" style={styles.title}>
          {t('member-actions')}
        </Text>
        {node.isOwner ? (
          <List.Item
            title={member.isAdmin ? t('remove-admin') : t('make-admin')}
            description={
              member.isAdmin
                ? t('remove-admin-description')
                : t('make-admin-description')
            }
            left={(props) => (
              <List.Icon
                {...props}
                icon={
                  member.isAdmin ? 'account-arrow-down' : 'account-arrow-up'
                }
              />
            )}
            onPress={() => setConfirmAction('toggleAdmin')}
          />
        ) : null}
        {node.isOwner ? (
          <List.Item
            title={t('transfer-ownership')}
            description={t('transfer-ownership-description')}
            left={(props) => (
              <List.Icon {...props} icon="account-arrow-right" />
            )}
            onPress={() => setConfirmAction('transfer')}
          />
        ) : null}
        {canRemoveThisMember ? (
          <List.Item
            title={t('remove-member')}
            description={t('remove-member-description')}
            left={(props) => <List.Icon {...props} icon="account-remove" />}
            onPress={() => setConfirmAction('remove')}
          />
        ) : null}
        <View style={styles.buttonContainer}>
          <Button onPress={onClose} mode="text">
            {t('cancel')}
          </Button>
        </View>
      </Modal>
    </Portal>
  )
}

const styles = StyleSheet.create({
  modalContainer: {
    margin: 20,
    padding: 20,
    borderRadius: 8,
  },
  title: {
    marginBottom: 16,
  },
  description: {
    marginBottom: 8,
  },
  warning: {
    marginBottom: 16,
    fontStyle: 'italic',
    opacity: 0.7,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 16,
  },
})

export default MemberActionsModal
